import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import {
  MapContainer,
  TileLayer,
  Circle,
  CircleMarker,
  Polygon,
  Popup,
  useMap,
  Tooltip,
  Marker,
  Polyline,
  ZoomControl,
} from "react-leaflet";
import { divIcon } from "leaflet"; // icono HTML (Bootstrap)
import "leaflet/dist/leaflet.css";

import {
  fetchPointControlsByYardApi,
  fetchYardsApi,
  GetPerimeterYard,
  GetRoundById,
  ListarPuntosControlResumen,
  urlBase,
} from "../../services/api";
import { HubConnectionState } from "@microsoft/signalr";
import { useAuth } from "../../context/AuthContext";
import RoundAccordion from "../rounds/RoundAccordion";
import { colorMaps } from "../../util/util";

// TTL local
const OFFLINE_AFTER_MS = 1 * 60 * 1000;  // 1 min → offline
const REMOVE_AFTER_MS = 10 * 60 * 1000; // 10 min → remove

// ---------- helpers ----------
async function ensureStarted(c) {
  switch (c.state) {
    case HubConnectionState.Disconnected:
      await c.start(); return;
    case HubConnectionState.Connecting:
    case HubConnectionState.Reconnecting:
      await new Promise((res) => {
        const poll = () => (c.state === HubConnectionState.Connected ? res() : setTimeout(poll, 200));
        poll();
      });
      return;
    case HubConnectionState.Connected: return;
  }
}
const ridOf = (m) => (m?.roundId ?? m?.RoundId ?? m?.id ?? m) || null;

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (Array.isArray(center) && center.length === 2) {
      try {
        try { if (map.stop) map.stop(); } catch { }
        try { map.invalidateSize(); } catch { }
        map.setView(center, 16, { animate: false });
      } catch { }
    }
  }, [center, map]);
  return null;
}
function FitToPolygon({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (Array.isArray(positions) && positions.length >= 3) {
      try {
        // Asegura dimensiones correctas del contenedor antes de ajustar
        try { map.invalidateSize(); } catch { }
        const execFit = () => {
          const size = map.getSize();
          const padX = Math.max(0, Math.round(size.x * 0.3));
          const padY = Math.max(0, Math.round(size.y * 0.3));
          map.fitBounds(positions, {
            paddingTopLeft: [padX, padY],
            paddingBottomRight: [padX, padY],
          });
        };
        // Ejecuta cuando el mapa esté listo (por si se remonta con key)
        if (map.whenReady) map.whenReady(execFit); else execFit();
        // console.log('[map.fitBounds]', { points: positions.length, padding: [padX, padY] });
      } catch { }
    }
  }, [positions, map]);
  return null;
}

// icono Bootstrap (verde/ámbar)
const ICON_ON = divIcon({ className: "leaflet-div-icon", html: `<i class="bi bi-person-fill" style="font-size:24px;color:#16a34a;"></i>`, iconSize: [24, 24], iconAnchor: [12, 24], popupAnchor: [0, -12] });
const ICON_OFF = divIcon({ className: "leaflet-div-icon", html: `<i class="bi bi-person-fill" style="font-size:24px;color:#f59e0b;"></i>`, iconSize: [24, 24], iconAnchor: [12, 24], popupAnchor: [0, -12] });

export default function RondasEnVivo() {
  const { token } = useAuth();

  // selector
  const [patios, setPatios] = useState([]);
  const [patioId, setPatioId] = useState(null);

  // mapa
  const [center, setCenter] = useState([-12.05, -77.05]);
  const [perimeter, setPerimeter] = useState([]); // [[lat,lng], ...]
  const [pcPoints, setPcPoints] = useState([]);   // [{id,name,lat,lng,geozoneRadio,order}]

  // rondas en vivo: { rid: { userName, lastPos, lastSeenTs, status } }
  const [rounds, setRounds] = useState({});

  // acordeón
  const [roundsInfo, setRoundsInfo] = useState({});   // { rid: {...} }
  const [roundPoints, setRoundPoints] = useState({}); // { rid: [...] } (array crudo)
  const [openRoundId, setOpenRoundId] = useState(null);

  // polilíneas: { rid: [[lat,lng], ...] }
  const [trails, setTrails] = useState({});

  // console.log("rondas neivov console log")

  // console.log(rounds)
  // mapeos
  const pcNameById = useMemo(
    () => Object.fromEntries((pcPoints || []).map(p => [p.id, p.name])),
    [pcPoints]
  );
  const pcAll = useMemo(() => {
    const arr = (pcPoints || []).map(p => ({ id: p.id, name: p.name, order: p.order ?? p.Order ?? 0 }));
    return arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [pcPoints]);

  // conexión
  const connRef = useRef(null);
  const prevPatioRef = useRef(null);
  const loadVerRef = useRef(0); // versión para perimetro/PCs
  const hubVerRef = useRef(0); // versión para hub/snapshot
  const userNameRef = useRef(null);

  // 1) Cargar patios
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const list = await fetchYardsApi({ token });
        // console.log("patios web")
        // console.log(list)
        const yds = (list || []).map(y => ({
          id: y.id ?? y.Id,
          name: y.nombre ?? y.name ?? y.Nombre ?? `Patio ${String(y.id ?? y.Id).slice(0, 8)}`,
          lat: y.lat ?? y.latitude ?? y.Latitude ?? null,
          lng: y.lng ?? y.longitude ?? y.Longitude ?? null,
        }));
        setPatios(yds);
        if (yds.length) {
          setPatioId(String(yds[0].id));
          if (yds[0].lat != null && yds[0].lng != null) setCenter([Number(yds[0].lat), Number(yds[0].lng)]);
        }
      } catch (e) { console.error("[patios] error:", e); }
    })();
  }, [token]);

  // 2) Cambiar de patio → perímetro + PCs 
  useEffect(() => {
    if (!token || !patioId) return;
    loadVerRef.current += 1;
    const ver = loadVerRef.current;

    setPerimeter([]); setPcPoints([]);
    setRounds({}); setRoundsInfo({}); setRoundPoints({}); setOpenRoundId(null);
    setTrails({});
    // Center tentatively to yard lat/lng if available
    try {
      const sel = (patios || []).find(p => String(p.id) === String(patioId));
      const lat0 = Number(sel?.lat), lng0 = Number(sel?.lng);
      if (Number.isFinite(lat0) && Number.isFinite(lng0)) setCenter([lat0, lng0]);
    } catch { }

    (async () => {
      try {
        // perímetro
        const rawPerim = await GetPerimeterYard({ yardId: patioId, token });
        if (loadVerRef.current !== ver) return;
        const periSorted = [...(rawPerim || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const periCoords = periSorted
          .map(p => [Number(p.latitude ?? p.Latitude), Number(p.longitude ?? p.Longitude)])
          .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
        // console.log('[perimeter]', { patioId, count: periCoords.length, sample: periCoords.slice(0, 3) });
        setPerimeter(periCoords);
        if (periCoords.length >= 3) {
          const acc = periCoords.reduce((a, [la, ln]) => [a[0] + la, a[1] + ln], [0, 0]);
          const cen = [acc[0] / periCoords.length, acc[1] / periCoords.length];
          // setCenter(cen);
        } else if (periCoords.length >= 1) {
          setCenter(periCoords[0]);
        }

        // PCs
        const rawPc = await fetchPointControlsByYardApi(patioId, { token });
        if (loadVerRef.current !== ver) return;
        const pcs = (rawPc || []).map(p => ({
          id: p.id ?? p.Id,
          name: p.name ?? p.Nombre ?? "PC",
          lat: Number(p.latitude ?? p.Latitude),
          lng: Number(p.longitude ?? p.Longitude),
          geozoneRadio: Number(p.geozoneRadio ?? p.GeozoneRadio ?? 10),
          order: Number(p.order ?? p.Order ?? 0),
        }))
          .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        // console.log('[pcPoints]', { patioId, count: pcs.length, sample: pcs.slice(0, 3) });
        setPcPoints(pcs);
        // Si no hubo perímetro o no tenía puntos válidos, centra al primer PC
        try {
          if ((!Array.isArray(periCoords) || periCoords.length === 0) && pcs.length > 0) {
            setCenter([pcs[0].lat, pcs[0].lng]);
          }
        } catch { }
      } catch (e) { console.warn("[patio] error perim/pc:", e); setPerimeter([]); setPcPoints([]); }
    })();
  }, [token, patioId]);

  // Reinicio inmediato al volver la red: si está Disconnected, iniciar y re-suscribir
  useEffect(() => {
    const onOnline = async () => {
      try {
        const c = connRef.current;
        if (!c) return;
        if (c.state === HubConnectionState.Disconnected) {
          await ensureStarted(c);
          const pid = prevPatioRef.current || patioId;
          if (pid != null) {
            try { await c.invoke("WatchPatio", String(pid)); } catch { }
            // snapshot rápido para no esperar GPS
            try {
              const snapshot = await c.invoke("GetActiveRounds", String(pid));
              setRounds(() => {
                const next = {};
                for (const it of snapshot || []) {
                  const rid = it.roundId ?? it.RoundId ?? it.id ?? null;
                  if (!rid) continue;
                  const lat = Number(it.lastLat ?? it.lastLatitude ?? it.Latitude);
                  const lng = Number(it.lastLng ?? it.lastLongitude ?? it.Longitude);
                  next[rid] = {
                    userName: it.userName ?? "",
                    lastPos: (Number.isFinite(lat) && Number.isFinite(lng)) ? [lat, lng] : null,
                    lastSeenTs: Date.now(),
                    status: it.online ? "online" : "offline"
                  };
                }
                return next;
              });
            } catch { }
          }
        }
      } catch { }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [patioId, token]);

  // 3) SignalR por patio
  useEffect(() => {
    if (!token || !patioId) return;

    (async () => {
      let c = connRef.current;
      if (!c) {
        c = new signalR.HubConnectionBuilder()
          .withUrl(`${urlBase}/hubs/patrol`, { accessTokenFactory: () => token, transport: signalR.HttpTransportType.WebSockets, skipNegotiation: true })
          .configureLogging(signalR.LogLevel.Information)
          .withAutomaticReconnect([0, 2000, 5000, 10000]).build();
        try {
          //  console.log('[hub] url:', `${urlBase}/hubs/patrol`); 
        } catch { }

        // handlers
        c.on("RoundStarted", (m) => {
          const rid = ridOf(m); if (!rid) return;
          setRounds(prev => ({ ...prev, [rid]: { userName: m.userName ?? prev[rid]?.userName ?? "", lastPos: prev[rid]?.lastPos ?? null, lastSeenTs: Date.now(), status: "online" } }));
        });

        c.on("PositionUpdated", (p) => {
          const rid = ridOf(p); if (!rid) return;
          const lat = Number(p.lat ?? p.latitude ?? p.Latitude), lng = Number(p.lng ?? p.longitude ?? p.Longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          setRounds(prev => {
            const r = prev[rid] ?? { userName: "", lastPos: null, status: "online" };
            return { ...prev, [rid]: { ...r, lastPos: [lat, lng], lastSeenTs: Date.now(), status: "online" } };
          });
          // polilínea (en memoria)
          setTrails(prev => {
            const arr = prev[rid] ? [...prev[rid]] : [];
            const last = arr[arr.length - 1];
            if (!last || last[0] !== lat || last[1] !== lng) arr.push([lat, lng]);
            return { ...prev, [rid]: arr.slice(-400) };
          });
        });

        c.on("RoundOffline", (m) => {
          const rid = ridOf(m); if (!rid) return;
          setRounds(prev => prev[rid] ? { ...prev, [rid]: { ...prev[rid], status: "offline" } } : prev);
        });

        c.on("RoundOnline", (m) => {
          const rid = ridOf(m); if (!rid) return;
          setRounds(prev => prev[rid] ? { ...prev, [rid]: { ...prev[rid], status: "online", lastSeenTs: Date.now() } } : prev);
        });

        c.on("RoundFinished", (m) => {
          const rid = ridOf(m); if (!rid) return;
          setRounds(prev => { const out = { ...prev }; delete out[rid]; return out; });
          setRoundsInfo(prev => { const out = { ...prev }; delete out[rid]; return out; });
          setRoundPoints(prev => { const out = { ...prev }; delete out[rid]; return out; });
          setTrails(prev => { const out = { ...prev }; delete out[rid]; return out; });
        });

        c.onreconnected(async () => {
          const pid = prevPatioRef.current;
          if (pid == null) return;
          try {
            // Re-join group after connection drop
            await c.invoke("WatchPatio", String(pid));

            // Refresh snapshot to avoid staying blank until new GPS arrives
            hubVerRef.current += 1;
            const ver = hubVerRef.current;
            const snapshot = await c.invoke("GetActiveRounds", String(pid));
            if (hubVerRef.current !== ver) return; // stale reconnected snapshot
            setRounds(() => {
              const next = {};
              for (const it of snapshot || []) {
                const rid = it.roundId ?? it.RoundId ?? it.id ?? null;
                if (!rid) continue;
                const lat = Number(it.lastLat ?? it.lastLatitude ?? it.Latitude);
                const lng = Number(it.lastLng ?? it.lastLongitude ?? it.Longitude);
                next[rid] = {
                  userName: it.userName ?? "",
                  lastPos: (Number.isFinite(lat) && Number.isFinite(lng)) ? [lat, lng] : null,
                  lastSeenTs: Date.now(),
                  status: it.online ? "online" : "offline"
                };
              }
              return next;
            });
          } catch { /* no-op */ }
        });

        connRef.current = c;
      }

      await ensureStarted(c);

      // (des)subscribirse
      if (prevPatioRef.current && prevPatioRef.current !== patioId) {
        try {
          // console.log('[hub] invoke UnwatchPatio', { patioId: String(prevPatioRef.current) }); 
          await c.invoke("UnwatchPatio", String(prevPatioRef.current));
        } catch { }
      }
      hubVerRef.current += 1;
      const ver = hubVerRef.current;

      setRounds({}); setRoundsInfo({}); setRoundPoints({}); setTrails({});

      // console.log('[hub] invoke WatchPatio', { patioId: String(patioId) });
      await c.invoke("WatchPatio", String(patioId));
      prevPatioRef.current = patioId;

      // snapshot
      try {
        // console.log('[hub] invoke GetActiveRounds', { patioId: String(patioId) });
        const snapshot = await c.invoke("GetActiveRounds", String(patioId));
        if (hubVerRef.current !== ver) return;
        setRounds(() => {
          const next = {};
          for (const it of snapshot || []) {
            const rid = it.roundId ?? it.RoundId ?? it.id ?? null;
            if (!rid) continue;
            const lat = Number(it.lastLat ?? it.lastLatitude ?? it.Latitude);
            const lng = Number(it.lastLng ?? it.lastLongitude ?? it.Longitude);
            next[rid] = {
              userName: it.userName ?? "",
              lastPos: (Number.isFinite(lat) && Number.isFinite(lng)) ? [lat, lng] : null,
              lastSeenTs: Date.now(),
              status: it.online ? "online" : "offline"
            };
          }
          return next;
        });
      } catch (e) { console.warn("[hub] snapshot error", e); }
    })().catch(err => console.error("[hub] connect/watch error", err));
  }, [token, patioId]);

  // 4) TTL local: offline / remove
  useEffect(() => {
    const id = setInterval(() => {
      setRounds(prev => {
        const now = Date.now(), out = { ...prev };
        for (const [rid, r] of Object.entries(prev)) {
          const seen = r.lastSeenTs ?? 0;
          if ((now - seen) > OFFLINE_AFTER_MS && out[rid]) out[rid].status = "offline";
          if ((now - seen) > REMOVE_AFTER_MS) {
            delete out[rid];
            setTrails(t => { const c = { ...t }; delete c[rid]; return c; });
          }
        }
        return out;
      });
    }, 5000);
    return () => clearInterval(id);
  }, []);

  // 5) Poll (las 2 query): GetRoundById + ListarPuntosControlResumen
  useEffect(() => {
    if (!token) return;
    const ids = Object.keys(rounds || {}).filter(rid => !!rid && rid !== "undefined" && rid !== "null");
    if (ids.length === 0) { setRoundsInfo({}); setRoundPoints({}); return; }

    let cancelled = false;
    const loadAll = async () => {
      for (const rid of ids) {
        try {
          const [info, status] = await Promise.all([
            GetRoundById({ id: rid, token }),
            ListarPuntosControlResumen({ roundId: rid, token })
            // console.log( await GetRoundById({ id: rid, token }))
          ]);
          userNameRef.current = info.user.lastName;
          if (cancelled) return;
          setRoundsInfo(prev => ({ ...prev, [rid]: info }));
          const arr = Array.isArray(status?.items) ? status.items : (Array.isArray(status) ? status : []);
          setRoundPoints(prev => ({ ...prev, [rid]: arr }));
        } catch { }
      }
    };
    loadAll();
    const iv = setInterval(loadAll, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [token, rounds]);

  // datos reducidos para el acordeón (cronómetro)
  const roundStateById = useMemo(() => {
    const out = {}; for (const [rid, r] of Object.entries(rounds || {})) out[rid] = { status: r.status, lastSeenTs: r.lastSeenTs ?? 0 };
    return out;
  }, [rounds]);
  const userNameById = useMemo(() => {
    const out = {}; for (const [rid, r] of Object.entries(rounds || {})) out[rid] = r.userName || "";
    return out;
  }, [rounds]);

  return (
    <div style={{ position: "relative", height: "90vh" }}>
      {/* Panel flotante (lado izquierdo) */}
      <aside
        className="position-absolute top-0 start-0 bg-white rounded shadow"
        style={{
          zIndex: 1000,
          margin: 12,
          padding: 12,
          width: 360,
          maxHeight: "calc(100% - 24px)",
          overflowY: "auto",
        }}
      >

        <label className="form-label">Seleccione un Patio</label>
        <select
          className="form-select"
          value={patioId ?? ""}
          onChange={e => setPatioId(e.target.value || null)}
          disabled={patios.length === 0}
        >
          {patios.length === 0 && <option value="">(sin patios)</option>}
          {patios.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
        </select>

        <div style={{ fontSize: 12, marginTop: 10, display: "flex", gap: 12 }}>
          <span><b style={{ color: "#16a34a", fontSize: "20px", margin: "auto" }}>● </b><span style={{ fontSize: "15px", margin: "auto" }}>online</span> </span>
          <span><b style={{ color: "#f59e0b", fontSize: "20px", margin: "auto" }}>● </b><span style={{ fontSize: "15px", margin: "auto" }}>offline</span> </span>
        </div>

        <RoundAccordion
          roundsInfo={roundsInfo}
          roundPoints={roundPoints}
          openId={openRoundId}
          setOpenId={setOpenRoundId}
          pcNameById={pcNameById}
          roundStateById={roundStateById}
          userNameById={userNameById}
          offlineAfterMs={OFFLINE_AFTER_MS}
          removeAfterMs={REMOVE_AFTER_MS}
          pcAll={pcAll}
        />
      </aside>

      {/* Mapa 100% del contenedor */}
      <main style={{ position: "absolute", inset: 0 }}>
        <MapContainer key={patioId} center={center} zoom={18} maxZoom={22} zoomControl={false} style={{ height: "100%", width: "100%" }}>
          <ZoomControl position="topright" />

          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {/* Recentrar siempre al cambiar el centro (independiente del perímetro) */}
          {/* <Recenter center={center} /> */}
          {/* Solo recentrar si NO hay perímetro; si hay, que mande FitToPolygon */}
          {!(Array.isArray(perimeter) && perimeter.length >= 3) && (
            <Recenter center={center} />
          )}

          {/* Perímetro */}
          {Array.isArray(perimeter) && perimeter.length >= 3 && (
            <Fragment>
              <Polygon positions={perimeter} pathOptions={{ color: "#2563EB", weight: 3, fillOpacity: 0.05 }} />
              <FitToPolygon positions={perimeter} />
            </Fragment>
          )}

          {/* Geozonas */}
          {pcPoints.map(pt => (
            <Circle
              key={`pc-zone-${pt.id}`}
              center={[pt.lat, pt.lng]}
              radius={Number(pt.geozoneRadio) || 0}
              pathOptions={{
                color: colorMaps.zoneStroke,
                fillColor: colorMaps.zoneFill,
                weight: 2.5,
                fillOpacity: 0.15
              }}
            />
          ))}

          {/* Centro del PC + etiqueta */}
          {pcPoints.map(pt => (
            <CircleMarker
              key={`pc-dot-${pt.id}`}
              center={[pt.lat, pt.lng]}
              radius={5}
              pathOptions={{ color: colorMaps.zoneCenter, weight: 2, fillColor: colorMaps.zoneCenter, fillOpacity: 1 }}
            >
              <Tooltip permanent direction="top" offset={[0, -10]} opacity={0.9} className="pc-tooltip">
                <p className="text-center fs-5 fw-semibold"> {pt.order}. {pcNameById[pt.id] || "PC"}</p>

              </Tooltip>
            </CircleMarker>
          ))}

          {/* Vigilantes en vivo (icono + trail en memoria) */}
          {Object.entries(rounds).map(([rid, r]) => (
            r.lastPos ? (
              <Fragment key={rid}>
                {/* polilínea si hay al menos 2 puntos */}
                {(trails[rid]?.length || 0) > 1 && (
                  <Polyline
                    positions={trails[rid]}
                    pathOptions={{
                      color: r.status === "online" ? "#22c55e" : "#f59e0b",
                      weight: 5,
                      opacity: 0.85
                    }}
                  />
                )}
                <Marker position={r.lastPos} icon={r.status === "online" ? ICON_ON : ICON_OFF}>
                  <Popup>
                    <div style={{ minWidth: 120 }} className="text-center">
                      <h6>{r.userName || "Vigilante"} {userNameRef.current || ""}</h6>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {r.status === "online" ? "Conectado" : "Sin señal"}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </Fragment>
            ) : null
          ))}
        </MapContainer>
      </main>
    </div>
  );
}
